import { useId } from 'react';
import { Sparkles } from 'lucide-react';

// SVG marks for the picker models. Each has the brand color
// inlined so there is no load flash. GeminiMark uses a useId-scoped gradient
// id to avoid id collisions when multiple ModelLogos render on the same page.

export function GeminiMark({ size = 26 }) {
  const gradId = `model-gemini-grad-${useId().replace(/:/g, '')}`;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Google Gemini">
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4285F4" />
          <stop offset="48%" stopColor="#9B72CB" />
          <stop offset="100%" stopColor="#D96570" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gradId})`}
        d="M12 24A14.304 14.304 0 0 0 0 12 14.304 14.304 0 0 0 12 0a14.305 14.305 0 0 0 12 12 14.305 14.305 0 0 0-12 12"
      />
    </svg>
  );
}

export function ClaudeMark({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Anthropic Claude">
      <path
        fill="#D97757"
        d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z"
      />
    </svg>
  );
}

export function OpenAIMark({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="OpenAI">
      <path
        fill="#0f172a"
        d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0805.0805 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"
      />
    </svg>
  );
}

// New: Kimi (Moonshot AI). Simple crescent-moon mark with brand teal.
export function KimiMark({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Moonshot Kimi">
      <circle cx="12" cy="12" r="11" fill="#0F766E" />
      <path
        fill="#FFFFFF"
        d="M14.5 6.5a6.5 6.5 0 1 0 0 11A5.5 5.5 0 0 1 14.5 6.5Z"
      />
    </svg>
  );
}

// New: DeepSeek. Whale-tail abstract with brand blue.
export function DeepSeekMark({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="DeepSeek">
      <circle cx="12" cy="12" r="11" fill="#1E40AF" />
      <path
        fill="#FFFFFF"
        d="M6 14c2-1 4-1 6 0s4 1 6 0v2c-2 1-4 1-6 0s-4-1-6 0Zm0-4c2-1 4-1 6 0s4 1 6 0v2c-2 1-4 1-6 0s-4-1-6 0Z"
      />
    </svg>
  );
}

// Neutral monograms, not vendor artwork.
function MonogramMark({ letters, size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10.5" fill="none" stroke="currentColor" />
      <text x="12" y="12" dy="0.35em" textAnchor="middle" fontSize="9"
        fontFamily="sans-serif" fill="currentColor">{letters}</text>
    </svg>
  );
}

export const XAIMark = (props) => <MonogramMark {...props} letters="X" />;
export const QwenMark = (props) => <MonogramMark {...props} letters="Q" />;
export const MistralMark = (props) => <MonogramMark {...props} letters="M" />;
export const MetaMark = (props) => <MonogramMark {...props} letters="Me" />;
export const ZaiMark = (props) => <MonogramMark {...props} letters="Z" />;

const LOGO_MAP = {
  ClaudeMark,
  GeminiMark,
  OpenAIMark,
  KimiMark,
  DeepSeekMark,
  XAIMark,
  QwenMark,
  MistralMark,
  MetaMark,
  ZaiMark,
};

export function ModelLogo({ logoComponent, size = 26 }) {
  const Cmp = LOGO_MAP[logoComponent];
  if (!Cmp) return <Sparkles size={size} />;
  return <Cmp size={size} />;
}
